import { apiClient } from '@/lib/api-client';

// Re-export shared canonical types. Existing call-sites import from
// '@/services/sync.service' so this keeps them working while centralising
// the source of truth in @bbpos/types.
export type {
  SyncConflictResolution,
  SyncEntityType,
  SyncOperation,
  SyncQueueStatus,
  SyncConflictSummaryRow,
  SyncPendingConflictCount,
} from '@bbpos/types';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SyncTransaction {
  client_id: string;
  // G2.17/G2.18: QUOTATION added. G2.22: BILL_ITEM removed — bills are atomic
  // and the backend rejects BILL_ITEM at the DTO level (G2.20).
  entity_type:
    | 'BILL'
    | 'STOCK_ADJUSTMENT'
    | 'PRODUCT'
    | 'EXPENSE'
    | 'CUSTOMER'
    | 'QUOTATION';
  operation: 'CREATE' | 'UPDATE' | 'DELETE';
  payload: Record<string, unknown>;
  device_id?: string;
}

export interface PushResult {
  processed: number;
  duplicates: number;
  conflicts: number;
  errors: number;
  results: Array<{
    client_id: string;
    status: string;
    server_id: string | null;
    error_message?: string;
    conflict_reason?: string;
    /** T19: a synced bill's server number (INV…). */
    bill_number?: string;
  }>;
}

export interface PullRecord {
  id: string;
  entity_type: string;
  operation: string;
  server_id: string;
  payload: Record<string, unknown>;
  synced_at: string;
}

export interface SyncConflict {
  id: string;
  entity_type: string;
  client_id: string;
  resolution: 'PENDING' | 'CLIENT_WINS' | 'SERVER_WINS' | 'MERGED' | 'DISCARDED';
  created_at: string;
}

export interface SyncConflictDetail extends SyncConflict {
  client_version: Record<string, unknown>;
  server_version: Record<string, unknown>;
  device_id: string | null;
  resolved_by?: string;
  resolved_at?: string;
  resolution_note?: string;
  /** VERSION or REJECTED_CREATE (sync plan Phase 3). */
  kind?: 'VERSION' | 'REJECTED_CREATE';
  /** The outcomes the API offers for this conflict. */
  allowed_resolutions?: string[];
  merged_version_suggestion?: Record<string, unknown> | null;
  resolution_error?: string | null;
}

export interface ResolveConflictResult {
  id: string;
  resolution: string;
  resolved_by: string;
  resolved_at: string;
  note: string | null;
}

export interface PendingConflictCount {
  pending_count: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface PullParams {
  since?: string;
  entity_type?: string;
  page?: number;
  limit?: number;
  /** Scope to one store — a till only needs its own location's records. */
  location_id?: string;
}

interface ConflictListParams {
  resolution?: string;
  page?: number;
  limit?: number;
}

// ─── Service ────────────────────────────────────────────────────────────────

export const syncService = {
  push(transactions: SyncTransaction[]) {
    return apiClient<PushResult>('/sync/push', {
      method: 'POST',
      body: { transactions },
    });
  },

  pull(params: PullParams = {}) {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        qs.set(key, String(value));
      }
    }
    const query = qs.toString();
    return apiClient<{ data: PullRecord[]; meta: PaginationMeta }>(
      `/sync/pull${query ? `?${query}` : ''}`,
    );
  },

  listConflicts(params: ConflictListParams = {}) {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        qs.set(key, String(value));
      }
    }
    const query = qs.toString();
    return apiClient<{ data: SyncConflict[]; meta: PaginationMeta }>(
      `/sync/conflicts${query ? `?${query}` : ''}`,
    );
  },

  getConflict(id: string) {
    return apiClient<SyncConflictDetail>(`/sync/conflicts/${id}`);
  },

  resolveConflict(id: string, data: { resolution: string; note?: string; merged_version?: Record<string, unknown> }) {
    return apiClient<ResolveConflictResult>(`/sync/conflicts/${id}/resolve`, {
      method: 'POST',
      body: data,
    });
  },

  // GAP-4 — CASHIER-visible pending conflict count
  getPendingConflictCount() {
    return apiClient<PendingConflictCount>('/sync/conflicts/count');
  },

  // PAGES.md §2.11 — clear all resolved (non-PENDING) conflicts
  clearResolvedConflicts() {
    return apiClient<{ deleted_count: number }>(
      '/sync/conflicts/clear-resolved',
      { method: 'POST' },
    );
  },
};
